import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import styled from 'styled-components';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import {
  collection,
  getDocs,
  doc,
  query,
  orderBy,
  where,
  writeBatch,
  updateDoc
} from 'firebase/firestore';
import { colors, spacing, borderRadius, typography } from '../../theme/colors';
import {
  FiDollarSign, FiPackage,
  FiSave, FiSearch, FiX, FiAlertCircle, FiCheckCircle,
  FiCalendar, FiDownload, FiUpload, FiTrash2
} from 'react-icons/fi';
import verifyAdminPassword from '../../utils/verifyAdminPassword';

// Import new architecture helpers
import { ensureOpeningBalanceTransaction, getAllOpeningBalanceTransactions, relinkOrphanOpeningToBranch, countMisplacedOpening, listMisplacedOpening, repairMisplacedOpening } from '../../utils/transactionHelper';
import { getSubTransactions } from '../../utils/subTransactionHelper';
import { getStockFlow, refreshItemCostSnapshots } from '../../utils/stockFlowHelper';
import { SYSTEM_ACCOUNTS } from '../../config/tranTypes';
import usePagePermissions from '../../hooks/usePagePermissions';

const OpeningBalances = () => {
  const { activeCompany, userProfile, activeBranch, currentUser, companyUser } = useAuth();
  const { canEdit } = usePagePermissions('masterData', 'openingBalances');
  const [activeTab, setActiveTab] = useState('accounts');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Financial Year states
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);

  // OB Transaction (from new architecture)
  const [obTransaction, setObTransaction] = useState(null);
  const [existingSubTransactions, setExistingSubTransactions] = useState([]);

  // Locations for inventory
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  // Master Data states - ALL accounts (Level 5 postable)
  const [accounts, setAccounts] = useState([]);
  const [items, setItems] = useState([]);

  // Opening Balances from SubTransactions/StockFlow (mapped by entityId)
  const [accountBalances, setAccountBalances] = useState({});
  const [inventoryBalances, setInventoryBalances] = useState({}); // grid: items at the SELECTED location only
  // ALL opening stock rows across every location (for the consolidated accounting total)
  const [allOBStock, setAllOBStock] = useState([]);
  // True when a branch is selected AND a null-branch opening exists (entered before the branch was created)
  const [orphanOpening, setOrphanOpening] = useState(false);
  const [relinking, setRelinking] = useState(false);
  // { accounts, stock } when lines of this branch sit inside another branch's
  // opening (saved there by mistake) — shown with a "Fix now" button.
  const [misplaced, setMisplaced] = useState(null);
  const [repairing, setRepairing] = useState(false);
  // Latest opening load wins: an older load that finishes late (after a quick
  // branch / year switch) must not put the previous branch's opening back.
  const loadSeqRef = useRef(0);
  const scopeKeyRef = useRef('');

  // Every save / import below writes into the opening on screen and tags the
  // lines with the selected branch. They must be the same branch — right after a
  // branch or year switch the screen briefly still holds the previous opening,
  // and saving then put this branch's lines inside the other branch's opening
  // (counted twice in reports). So writes wait until the right one is loaded.
  const screenBranchId = activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null;
  const obReady = !loading && !!obTransaction?.id
    && (obTransaction.branchId || null) === screenBranchId
    && (!obTransaction.financialYearId || obTransaction.financialYearId === selectedYearId);
  const openingNotReady = () => {
    if (obReady) return false;
    alert('The opening of the selected branch is still loading. Please wait a moment and try again.');
    return true;
  };

  // Modified balances tracking (unsaved changes)
  const [modifiedAccounts, setModifiedAccounts] = useState({});
  const [modifiedItems, setModifiedItems] = useState({});

  // Opening-stock Excel import (Inventory tab)
  const [importingOB, setImportingOB] = useState(false);
  const obFileInputRef = useRef(null);

  // Clear-opening-inventory modal (admin password approval)
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearPassword, setClearPassword] = useState('');
  const [clearBusy, setClearBusy] = useState(false);

  const defaultLocationId = useMemo(
    () => locations.find(l => l.isDefault)?.id || locations[0]?.id || '',
    [locations]
  );

  // Build the grid for the SELECTED location only. Switching the top location
  // reloads that location's per-item quantities (multi-location split). Legacy
  // rows with no location fall back to the default location so they never hide.
  useEffect(() => {
    const map = {};
    allOBStock.forEach(sf => {
      if (!sf.itemId) return;
      const rowLoc = sf.locationId || defaultLocationId || '';
      if (selectedLocationId && rowLoc !== selectedLocationId) return;
      const enteredQty = sf.enteredQuantity != null ? sf.enteredQuantity : (sf.quantity || 0);
      const enteredCost = sf.enteredUnitCost != null ? sf.enteredUnitCost : (sf.unitCost || 0);
      // Batch-tracked items can have MANY opening rows (one per batch). Collect
      // them all under the item instead of overwriting (the old `map[itemId] =`
      // kept only the last row, which is why a second opening batch vanished).
      if (sf.trackingType === 'Batch') {
        const entry = map[sf.itemId] || {
          trackingType: 'Batch', locationId: rowLoc,
          unitCode: sf.enteredUnitCode || sf.unitOfMeasure || '', batches: []
        };
        entry.batches.push({
          key: sf.id, stockFlowId: sf.id,
          quantity: enteredQty, unitCost: enteredCost,
          batchNumber: sf.batchNumber || '', expiryDate: sf.expiryDate || ''
        });
        map[sf.itemId] = entry;
      } else {
        map[sf.itemId] = {
          stockFlowId: sf.id, quantity: enteredQty, unitCost: enteredCost, totalCost: enteredQty * enteredCost,
          unitCode: sf.enteredUnitCode || sf.unitOfMeasure || '', locationId: rowLoc,
          trackingType: sf.trackingType || 'None', batchNumber: sf.batchNumber || '',
          expiryDate: sf.expiryDate || '', serialNumbers: sf.serialNumbers || []
        };
      }
    });
    setInventoryBalances(map);
    setModifiedItems({}); // discard unsaved inventory edits when the location changes
  }, [allOBStock, selectedLocationId, defaultLocationId]);

  // Load financial years on mount
  useEffect(() => {
    if (activeCompany?.id) {
      fetchFinancialYears();
      fetchLocations();
    }
  }, [activeCompany]);

  // Load data when financial year or branch changes
  useEffect(() => {
    if (selectedYearId && activeCompany?.id) {
      // A different branch or year is a different opening: drop the old one at
      // once, so nothing can be saved into it while the new one is loading.
      const scopeKey = `${activeCompany.id}|${selectedYearId}|${activeBranch?.id || ''}`;
      if (scopeKeyRef.current && scopeKeyRef.current !== scopeKey) {
        setObTransaction(null);
        setExistingSubTransactions([]);
        setAccountBalances({});
        setAllOBStock([]);
        setModifiedAccounts({});
        setModifiedItems({});
        setOrphanOpening(false);
        setMisplaced(null);
      }
      scopeKeyRef.current = scopeKey;
      fetchAllData();
    }
  }, [selectedYearId, activeCompany, activeBranch]);

  const fetchFinancialYears = async () => {
    try {
      const yearsRef = collection(db, `companies/${activeCompany.id}/financialYears`);
      const q = query(yearsRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const years = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFinancialYears(years);

      // Auto-select active year
      const activeYear = years.find(y => y.isActive);
      if (activeYear) {
        setSelectedYearId(activeYear.id);
        setSelectedYear(activeYear);
      } else if (years.length > 0) {
        setSelectedYearId(years[0].id);
        setSelectedYear(years[0]);
      }
    } catch (error) {
      console.error('Error fetching financial years:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const locationsRef = collection(db, `companies/${activeCompany.id}/inventoryLocations`);
      const q = query(locationsRef, where('isActive', '==', true));
      const snapshot = await getDocs(q);
      const locs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLocations(locs);

      // Set default location
      const defaultLoc = locs.find(l => l.isDefault) || locs[0];
      if (defaultLoc) {
        setSelectedLocationId(defaultLoc.id);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleYearChange = (yearId) => {
    setSelectedYearId(yearId);
    const year = financialYears.find(y => y.id === yearId);
    setSelectedYear(year);
    // Clear unsaved changes when switching years
    setModifiedAccounts({});
    setModifiedItems({});
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAccounts(),
        fetchItems(),
        fetchOpeningBalances()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const fetchAccounts = async () => {
    try {
      const accountsRef = collection(db, `companies/${activeCompany.id}/accounts`);
      const snapshot = await getDocs(accountsRef);
      const fetchedAccounts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter to ONLY Level 5 postable accounts (ALL types - assets, liabilities, equity)
      // Include customer/vendor/bank linked accounts
      const permanentAccountTypes = ['asset', 'liability', 'equity'];
      const level5Accounts = fetchedAccounts.filter(a => {
        const level = parseInt(a.level) || 0;
        const accountType = (a.accountType || '').toLowerCase();
        return level === 5 && permanentAccountTypes.includes(accountType) && a.isPostable !== false;
      });

      // Sort by code
      level5Accounts.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
      setAccounts(level5Accounts);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    }
  };

  const fetchItems = async () => {
    try {
      const q = query(
        collection(db, `companies/${activeCompany.id}/inventoryItems`),
        orderBy('itemName')
      );
      const snapshot = await getDocs(q);
      setItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(i => i.isDeleted !== true));
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  /**
   * Fetch Opening Balances — branch-aware
   * Each branch gets its own OB transaction.
   * Non-branch companies get a shared OB (branchId: null).
   */
  const fetchOpeningBalances = async () => {
    const seq = ++loadSeqRef.current;
    const stale = () => seq !== loadSeqRef.current;
    try {
      // Guard: must select specific branch if branches are active
      if (activeBranch && activeBranch.id === 'all') {
        setObTransaction(null);
        setAccountBalances({});
        setInventoryBalances({});
        setAllOBStock([]);
        setMisplaced(null);
        return;
      }

      const currentBranchId = activeBranch?.id || null;
      const currentBranchName = activeBranch?.branchName || null;

      // Get or create branch-specific OB transaction
      const obTran = await ensureOpeningBalanceTransaction(
        activeCompany.id,
        selectedYearId,
        selectedYear?.startDate,
        userProfile?.uid,
        currentBranchId,
        currentBranchName
      );
      if (stale()) return;

      // Detect the "branch added after opening was entered" case: a real branch
      // is selected but a null-branch OB still exists → offer to move it in.
      // Same list: lines of THIS branch saved inside another branch's opening
      // (counted on the server — a few reads) → offer "Fix now".
      try {
        const allOBs = await getAllOpeningBalanceTransactions(activeCompany.id, selectedYearId);
        if (stale()) return;
        setOrphanOpening(!!currentBranchId && allOBs.some(t => !t.branchId));
        if (currentBranchId) {
          countMisplacedOpening(activeCompany.id, selectedYearId, currentBranchId, allOBs)
            .then(c => { if (!stale()) setMisplaced(c.accounts + c.stock > 0 ? c : null); })
            .catch(e => { console.warn('Misplaced opening check failed:', e && e.message); if (!stale()) setMisplaced(null); });
        } else {
          setMisplaced(null);
        }
      } catch { if (!stale()) setOrphanOpening(false); }

      if (!obTran?.id) {
        setObTransaction(obTran);
        console.log('No OB transaction found/created');
        return;
      }

      // Fetch SubTransactions for THIS OB transaction only
      const subTrans = await getSubTransactions(activeCompany.id, obTran.id);
      // Filter by branchId for extra safety (in case of shared OB legacy)
      const filteredSubTrans = currentBranchId
        ? subTrans.filter(st => st.branchId === currentBranchId || !st.branchId)
        : subTrans;

      // Fetch StockFlow for THIS OB transaction only
      const stockFlowEntries = await getStockFlow(activeCompany.id, obTran.id);
      if (stale()) return;
      // The opening and its lines are shown together, only once all are loaded.
      setObTransaction(obTran);
      setExistingSubTransactions(filteredSubTrans);
      const filteredStockFlow = currentBranchId
        ? stockFlowEntries.filter(sf => sf.branchId === currentBranchId || !sf.branchId)
        : stockFlowEntries;

      // Map SubTransactions to balances by accountId
      const accBalances = {};
      filteredSubTrans.forEach(st => {
        if (st.accountId) {
          accBalances[st.accountId] = {
            subTransactionId: st.id,
            debitAmount: st.debitAmount || 0,
            creditAmount: st.creditAmount || 0
          };
        }
      });
      setAccountBalances(accBalances);

      // Keep ALL opening stock rows (every location). The grid for the selected
      // location is derived from this by the location effect; the consolidated
      // accounting total is also computed from this (all locations together).
      setAllOBStock(filteredStockFlow);

    } catch (error) {
      console.error('Error fetching opening balances:', error);
    }
  };

  // Handle account balance change
  const handleAccountBalanceChange = (accountId, field, value) => {
    setModifiedAccounts(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  // Handle item balance change
  const handleItemBalanceChange = (itemId, field, value) => {
    setModifiedItems(prev => {
      const current = prev[itemId] || {};
      const item = items.find(i => i.id === itemId);

      // For string/array fields, don't parse as number
      const isStringField = ['batchNumber', 'expiryDate', 'serialText', 'unitCode'].includes(field);
      const isArrayField = field === 'serialNumbers';

      // Quantity is decimal-capable (weight / SQft / fractional units, and the
      // company-level Quantity Decimal Places setting) — parseInt here silently
      // chopped "2.5" to "2". The Excel import already used parseFloat, so manual
      // entry was the odd one out.
      const updated = {
        ...current,
        [field]: isStringField ? value : isArrayField ? value : (parseFloat(value) || 0)
      };

      // Auto-set tracking type from item master when modifying
      if (item?.trackingType && !updated.trackingType) {
        updated.trackingType = item.trackingType;
      }

      return { ...prev, [itemId]: updated };
    });
  };

  // ── Multi-batch opening (batch-tracked items only) ─────────────────────────
  // The money value of an opening entry — batch items sum their batch lines,
  // everything else is the single qty × cost. Used everywhere a total is needed
  // so batch items are counted correctly.
  const entryOpeningValue = (entry) => {
    if (!entry) return 0;
    if (Array.isArray(entry.batches)) {
      return entry.batches.reduce((s, b) => s + (Number(b.quantity) || 0) * (Number(b.unitCost) || 0), 0);
    }
    return (Number(entry.quantity) || 0) * (Number(entry.unitCost) || 0);
  };

  // Stable, collision-free keys for freshly-added batch rows.
  const batchKeyRef = useRef(0);
  const nextBatchKey = () => `new_${++batchKeyRef.current}`;

  // The batch list to show/edit for an item: the in-progress edit if any,
  // otherwise a copy seeded from what's saved (migrating a legacy single-batch
  // entry into one line so old data stays editable). Called during render, so
  // seed keys are DETERMINISTIC ('b0' / the stockFlowId) — never nextBatchKey(),
  // which would change every render and make inputs lose focus.
  const getEditableBatches = (itemId) => {
    const mod = modifiedItems[itemId];
    if (Array.isArray(mod?.batches)) return mod.batches;
    const stored = inventoryBalances[itemId];
    if (Array.isArray(stored?.batches)) return stored.batches.map(b => ({ ...b }));
    // Any single saved row for this (batch-tracked) item — including a legacy row
    // or a previous-year carry-forward that was written without batch fields —
    // becomes one editable batch line so its qty/cost isn't lost and the user can
    // fill in / split the batch number.
    if (stored && stored.stockFlowId) {
      return [{
        key: stored.stockFlowId, stockFlowId: stored.stockFlowId,
        quantity: stored.quantity || 0, unitCost: stored.unitCost || 0,
        batchNumber: stored.batchNumber || '', expiryDate: stored.expiryDate || ''
      }];
    }
    return [{ key: 'b0', quantity: 0, unitCost: 0, batchNumber: '', expiryDate: '' }];
  };

  const setItemBatches = (itemId, batches) => {
    setModifiedItems(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), trackingType: 'Batch', batches }
    }));
  };

  // Handlers work by INDEX (stable within a render) so they never depend on key
  // matching between the displayed list and a freshly-seeded copy.
  const handleBatchFieldChange = (itemId, idx, field, value) => {
    const isText = field === 'batchNumber' || field === 'expiryDate';
    const next = getEditableBatches(itemId).map((b, i) =>
      i === idx ? { ...b, [field]: isText ? value : (parseFloat(value) || 0) } : b
    );
    setItemBatches(itemId, next);
  };

  const addBatchRow = (itemId) => {
    setItemBatches(itemId, [
      ...getEditableBatches(itemId),
      { key: nextBatchKey(), quantity: 0, unitCost: 0, batchNumber: '', expiryDate: '' }
    ]);
  };

  const removeBatchRow = (itemId, idx) => {
    const remaining = getEditableBatches(itemId).filter((_, i) => i !== idx);
    // Never leave zero rows — keep one blank line for continued editing.
    setItemBatches(itemId, remaining.length ? remaining : [{ key: nextBatchKey(), quantity: 0, unitCost: 0, batchNumber: '', expiryDate: '' }]);
  };

  /**
   * Save Account Opening Balances
   * Creates SubTransactions for each account with a balance
   */
  const saveAccountBalances = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (Object.keys(modifiedAccounts).length === 0) {
      alert('No changes to save');
      return;
    }

    if (!selectedYearId || !obTransaction?.id) {
      alert('Please select a financial year');
      return;
    }

    if (activeBranch && activeBranch.id === 'all') {
      alert('Please select a specific branch before saving opening balances.');
      return;
    }
    if (openingNotReady()) return;

    // Opening balances should tie. Warn (not hard-block, per the flexible design)
    // if they don't, so the user can add the balancing Capital / Opening Balance
    // Equity entry instead of silently saving an opening that makes the Trial
    // Balance and Balance Sheet not balance.
    const obTotals = accounts.reduce((acc, a) => {
      const m = modifiedAccounts[a.id] || {};
      const s = accountBalances[a.id] || {};
      acc.debit += (m.debitAmount ?? s.debitAmount ?? 0);
      acc.credit += (m.creditAmount ?? s.creditAmount ?? 0);
      return acc;
    }, { debit: 0, credit: 0 });
    if (Math.abs(obTotals.debit - obTotals.credit) >= 0.01) {
      const diff = Math.abs(obTotals.debit - obTotals.credit);
      if (!window.confirm(
        `Opening balances do NOT balance.\n\n` +
        `Debits ${obTotals.debit.toFixed(2)} vs Credits ${obTotals.credit.toFixed(2)} (difference ${diff.toFixed(2)}).\n\n` +
        `An unbalanced opening makes your Trial Balance and Balance Sheet not tie. ` +
        `Add the balancing entry (e.g. Capital / Opening Balance Equity) to fix it.\n\nSave anyway?`
      )) {
        return;
      }
    }

    setSaving(true);
    try {
      const batch = writeBatch(db);
      const subTransRef = collection(db, `companies/${activeCompany.id}/subTransactions`);
      const timestamp = new Date().toISOString();

      for (const [accountId, balances] of Object.entries(modifiedAccounts)) {
        const existingBalance = accountBalances[accountId];
        const account = accounts.find(a => a.id === accountId);

        const debit = balances.debitAmount ?? existingBalance?.debitAmount ?? 0;
        const credit = balances.creditAmount ?? existingBalance?.creditAmount ?? 0;

        // Skip if both are zero
        if (debit === 0 && credit === 0) {
          // Delete existing if any
          if (existingBalance?.subTransactionId) {
            const docRef = doc(db, `companies/${activeCompany.id}/subTransactions`, existingBalance.subTransactionId);
            batch.delete(docRef);
          }
          continue;
        }

        const subTransData = {
          transactionId: obTransaction.id,
          tranTypeCode: 'OB',
          docNumber: obTransaction.docNumber,
          docDate: obTransaction.docDate,
          financialYearId: selectedYearId,
          branchId: activeBranch?.id || null,
          branchName: activeBranch?.branchName || null,
          lineNumber: 0,
          accountId: accountId,
          accountCode: account?.code || '',
          accountName: account?.name || '',
          description: `Opening Balance - ${account?.name || 'Account'}`,
          debitAmount: debit,
          creditAmount: credit,
          // Add party info if it's a linked account. The COA stores the entity
          // name as part of the account name itself ("Customer: ABC", "Vendor: X",
          // "Bank: HBL - Main", "Staff: Ali"), so we recover partyName by stripping
          // the prefix instead of querying the source entity.
          partyType: account?.linkedEntityType || null,
          partyId: account?.linkedEntityId || null,
          partyName: account?.linkedEntityType && account?.name
            ? account.name.replace(/^(Customer|Vendor|Bank|Staff):\s*/, '')
            : null,
          status: 'Posted', // Opening balances are immediately posted
          updatedAt: timestamp,
          updatedBy: userProfile?.uid
        };

        if (existingBalance?.subTransactionId) {
          // Update existing
          const docRef = doc(db, `companies/${activeCompany.id}/subTransactions`, existingBalance.subTransactionId);
          batch.update(docRef, subTransData);
        } else {
          // Create new
          subTransData.createdAt = timestamp;
          subTransData.createdBy = userProfile?.uid;
          const newDocRef = doc(subTransRef);
          batch.set(newDocRef, subTransData);
        }
      }

      // Update parent OB transaction status to 'Posted' (branchId already set at creation)
      const obDocRef = doc(db, `companies/${activeCompany.id}/transactions`, obTransaction.id);
      batch.update(obDocRef, {
        status: 'Posted',
        postedAt: timestamp,
        postedBy: userProfile?.uid,
        updatedAt: timestamp,
        updatedBy: userProfile?.uid
      });

      await batch.commit();
      setModifiedAccounts({});
      await fetchOpeningBalances();
      alert('Account opening balances saved successfully!');
    } catch (error) {
      console.error('Error saving account balances:', error);
      alert('Failed to save account balances: ' + error.message);
    }
    setSaving(false);
  };

  // Move a null-branch opening (entered before this branch existed) into the
  // selected branch — stock + accounts — and drop the empty duplicate OB.
  const handleRelinkOrphan = async () => {
    if (!canEdit) { alert('You do not have permission to make changes.'); return; }
    const currentBranchId = activeBranch?.id || null;
    if (!currentBranchId || currentBranchId === 'all') { alert('Select a specific branch first.'); return; }
    if (!selectedYearId) { alert('Select a financial year first.'); return; }
    if (!window.confirm(`Move the opening balances that aren't linked to any branch into "${activeBranch?.branchName}"?\n\nThis re-tags the existing opening (stock + accounts) to this branch. Existing data is not deleted.`)) return;
    setRelinking(true);
    try {
      const res = await relinkOrphanOpeningToBranch(activeCompany.id, selectedYearId, currentBranchId, activeBranch?.branchName || null);
      if (res.moved) {
        alert(`Done. Opening moved into "${activeBranch?.branchName}" — ${res.counts.stockFlow} stock row(s), ${res.counts.subTransactions} account entr(ies)${res.dupRemoved ? ', and 1 empty duplicate removed' : ''}.`);
      } else {
        alert('Nothing to move — no unlinked opening found.');
      }
      setOrphanOpening(false);
      await fetchOpeningBalances();
    } catch (e) {
      console.error('Relink orphan opening failed:', e);
      alert('Failed to move opening: ' + e.message);
    } finally {
      setRelinking(false);
    }
  };

  // Put right this branch's opening lines that were saved inside another
  // branch's opening by mistake: duplicates are removed, anything else is moved
  // into this branch's own opening. Other branches' own lines are not touched.
  const handleRepairMisplaced = async () => {
    if (!canEdit) { alert('You do not have permission to make changes.'); return; }
    const currentBranchId = activeBranch?.id || null;
    if (!currentBranchId || currentBranchId === 'all') { alert('Select a specific branch first.'); return; }
    if (openingNotReady()) return;
    const n = (misplaced?.accounts || 0) + (misplaced?.stock || 0);
    if (!window.confirm(
      `${n} opening entr(ies) of "${activeBranch?.branchName}" were saved inside another branch's opening by mistake, so reports count this branch's opening twice.\n\n` +
      `Fix now will:\n` +
      `• remove the ones this branch's opening already has (duplicates)\n` +
      `• move any others into this branch's opening\n\n` +
      `Other branches' own openings are not touched. Continue?`
    )) return;
    setRepairing(true);
    try {
      const res = await repairMisplacedOpening(activeCompany.id, selectedYearId, currentBranchId, activeBranch?.branchName || null);
      if (res.itemIds.length) {
        try {
          await refreshItemCostSnapshots(activeCompany.id, res.itemIds, selectedYearId);
        } catch (snapErr) {
          console.error('Item cost snapshot refresh failed:', snapErr);
        }
      }
      setMisplaced(null);
      await fetchOpeningBalances();
      alert(
        `Done for "${activeBranch?.branchName}".\n\n` +
        `Duplicates removed: ${res.removed.accounts} account entr(ies), ${res.removed.stock} stock row(s).\n` +
        `Moved into this branch's opening: ${res.moved.accounts} account entr(ies), ${res.moved.stock} stock row(s).`
      );
    } catch (e) {
      console.error('Repair misplaced opening failed:', e);
      alert('Fix failed: ' + e.message);
    } finally {
      setRepairing(false);
    }
  };

  /**
   * Save Inventory Opening Balances
   * Creates BOTH SubTransactions (for accounting) AND StockFlow (for inventory)
   */
  const saveItemBalances = async (overrideItems = null) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    // overrideItems is supplied by the Excel import path; the manual grid uses
    // the tracked modifiedItems state. Same posting engine either way.
    const modItems = overrideItems || modifiedItems;
    if (Object.keys(modItems).length === 0) {
      alert('No changes to save');
      return;
    }

    if (!selectedYearId || !obTransaction?.id) {
      alert('Please select a financial year');
      return;
    }

    if (activeBranch && activeBranch.id === 'all') {
      alert('Please select a specific branch before saving opening balances.');
      return;
    }
    if (openingNotReady()) return;

    // Get default location if not selected
    let locationId = selectedLocationId;
    let locationName = '';
    if (!locationId && locations.length > 0) {
      const defaultLoc = locations.find(l => l.isDefault) || locations[0];
      locationId = defaultLoc.id;
      locationName = defaultLoc.locationName;
    } else {
      const loc = locations.find(l => l.id === locationId);
      locationName = loc?.locationName || 'Default';
    }

    // Validate batch/serial tracking for modified items
    for (const [itemId, values] of Object.entries(modItems)) {
      const item = items.find(i => i.id === itemId);

      if (item?.trackingType === 'Batch') {
        // Multi-batch: every batch line with a quantity needs a batch number,
        // and no two batch lines on one item may share the same number.
        if (Array.isArray(values.batches)) {
          const active = values.batches.filter(b => (Number(b.quantity) || 0) > 0);
          for (const b of active) {
            if (!String(b.batchNumber || '').trim()) {
              alert(`Batch number is required on every batch line of "${item.itemName}".`);
              return;
            }
          }
          const nums = active.map(b => String(b.batchNumber).trim().toLowerCase());
          if (new Set(nums).size !== nums.length) {
            alert(`"${item.itemName}" has the same batch number on more than one line. Batch numbers must be unique.`);
            return;
          }
          continue; // batch item validated
        }
        // Legacy single-batch / import path
        const quantity = values.quantity ?? inventoryBalances[itemId]?.quantity ?? 0;
        if (quantity > 0 && !String(values.batchNumber ?? '').trim()) {
          alert(`Batch number is required for "${item.itemName}" (Batch tracked item)`);
          return;
        }
        continue;
      }

      const quantity = values.quantity ?? inventoryBalances[itemId]?.quantity ?? 0;
      if (quantity <= 0) continue; // Skip zero-qty items
      if (item?.trackingType === 'Serial') {
        const serialNumbers = values.serialNumbers || [];
        if (serialNumbers.length !== quantity) {
          alert(`"${item.itemName}" requires exactly ${quantity} serial number(s). You entered ${serialNumbers.length}.`);
          return;
        }
        const uniqueSerials = new Set(serialNumbers);
        if (uniqueSerials.size !== serialNumbers.length) {
          alert(`Duplicate serial numbers found for "${item.itemName}"`);
          return;
        }
      }
    }

    // Find Inventory Control account
    const inventoryControlAccount = accounts.find(a => a.code === SYSTEM_ACCOUNTS.INVENTORY_CONTROL);
    if (!inventoryControlAccount) {
      alert('Inventory Control account not found. Please ensure Chart of Accounts is properly set up.');
      return;
    }

    setSaving(true);
    try {
      // Firestore caps a WriteBatch at 500 ops. Big inventories (bulk import /
      // clear-all) can exceed that, so queue ops across auto-rotated batches.
      const fullBatches = [];
      let currentBatch = writeBatch(db);
      let opCount = 0;
      const batch = {
        set: (ref, data) => { if (opCount >= 450) { fullBatches.push(currentBatch); currentBatch = writeBatch(db); opCount = 0; } opCount++; currentBatch.set(ref, data); },
        update: (ref, data) => { if (opCount >= 450) { fullBatches.push(currentBatch); currentBatch = writeBatch(db); opCount = 0; } opCount++; currentBatch.update(ref, data); },
        delete: (ref) => { if (opCount >= 450) { fullBatches.push(currentBatch); currentBatch = writeBatch(db); opCount = 0; } opCount++; currentBatch.delete(ref); },
        commit: async () => { fullBatches.push(currentBatch); for (const b of fullBatches) await b.commit(); }
      };
      const subTransRef = collection(db, `companies/${activeCompany.id}/subTransactions`);
      const stockFlowRef = collection(db, `companies/${activeCompany.id}/stockFlow`);
      const timestamp = new Date().toISOString();

      let totalInventoryValue = 0;

      for (const [itemId, values] of Object.entries(modItems)) {
        const existingBalance = inventoryBalances[itemId];
        const item = items.find(i => i.id === itemId);

        // ── Batch-tracked items: write ONE stockFlow row per batch line ──
        // The manual grid supplies `values.batches`; the Excel-import / legacy
        // path supplies flat fields (treated as a single batch). Removed batch
        // lines are deleted; the item's total value is the sum of its batches.
        if (item?.trackingType === 'Batch') {
          const itmUnitsB = Array.isArray(item?.units) ? item.units : [];
          const baseRowB = itmUnitsB.find(u => u.isBase) || itmUnitsB[0] || null;
          const baseUnitCodeB = baseRowB?.unitCode || item?.unitOfMeasure || 'Pcs';
          const enteredUnitCodeB = values.unitCode ?? existingBalance?.unitCode ?? baseUnitCodeB;
          const unitRowB = itmUnitsB.find(u => u.unitCode === enteredUnitCodeB) || baseRowB;
          const conversionB = Number(unitRowB?.conversion) || 1;

          const incoming = Array.isArray(values.batches)
            ? values.batches
            : [{
                stockFlowId: existingBalance?.batches?.[0]?.stockFlowId,
                batchNumber: values.batchNumber ?? existingBalance?.batches?.[0]?.batchNumber ?? '',
                quantity: values.quantity ?? 0,
                unitCost: values.unitCost ?? 0,
                expiryDate: values.expiryDate ?? null
              }];

          // Delete any previously-saved batch the user removed entirely.
          const referenced = new Set(incoming.map(b => b.stockFlowId).filter(Boolean));
          (existingBalance?.batches || []).forEach(sb => {
            if (sb.stockFlowId && !referenced.has(sb.stockFlowId)) {
              batch.delete(doc(db, `companies/${activeCompany.id}/stockFlow`, sb.stockFlowId));
            }
          });

          for (const b of incoming) {
            const eq = Number(b.quantity) || 0;
            const ec = Number(b.unitCost) || 0;
            if (eq <= 0) {
              // Zeroed-out batch → remove its row if it had one.
              if (b.stockFlowId) batch.delete(doc(db, `companies/${activeCompany.id}/stockFlow`, b.stockFlowId));
              continue;
            }
            const baseQty = eq * conversionB;
            const baseCost = conversionB > 0 ? ec / conversionB : ec;
            const tc = baseQty * baseCost;
            totalInventoryValue += tc;

            const sfData = {
              transactionId: obTransaction.id, tranTypeCode: 'OB',
              docNumber: obTransaction.docNumber, docDate: obTransaction.docDate,
              financialYearId: selectedYearId,
              branchId: activeBranch?.id || null, branchName: activeBranch?.branchName || null,
              lineNumber: 0, itemId: itemId, itemCode: item?.itemCode || '', itemName: item?.itemName || '',
              description: `Opening Stock - ${item?.itemName || 'Item'}`,
              locationId: locationId, locationName: locationName, direction: 'in',
              quantity: baseQty, unitOfMeasure: baseUnitCodeB,
              enteredQuantity: eq, enteredUnitCode: enteredUnitCodeB, enteredUnitCost: ec, enteredUnitPrice: 0,
              conversion: conversionB, baseUnitCode: baseUnitCodeB,
              costingMethod: item?.costingMethod || 'weightedAverage',
              unitCost: baseCost, totalCost: tc,
              unitPrice: 0, grossAmount: 0, discountPercent: 0, discountAmount: 0, netAmount: 0,
              taxable: false, taxRate: 0, taxAmount: 0, lineTotal: 0, profitAmount: 0,
              trackingType: 'Batch', batchNumber: b.batchNumber || '', expiryDate: b.expiryDate || null,
              serialNumbers: [], status: 'Posted', updatedAt: timestamp, updatedBy: userProfile?.uid
            };
            if (b.stockFlowId) {
              batch.update(doc(db, `companies/${activeCompany.id}/stockFlow`, b.stockFlowId), sfData);
            } else {
              sfData.createdAt = timestamp; sfData.createdBy = userProfile?.uid;
              batch.set(doc(stockFlowRef), sfData);
            }
          }
          continue;
        }

        const enteredQuantity = values.quantity ?? existingBalance?.quantity ?? 0;
        const enteredUnitCost = values.unitCost ?? existingBalance?.unitCost ?? 0;

        // Resolve entered unit + base unit from item.units[]. Convert qty/cost
        // to base before writing stockFlow so WAVG and balances stay coherent.
        const itmUnits = Array.isArray(item?.units) ? item.units : [];
        const baseRow = itmUnits.find(u => u.isBase) || itmUnits[0] || null;
        const baseUnitCode = baseRow?.unitCode || item?.unitOfMeasure || 'Pcs';
        const enteredUnitCode = values.unitCode ?? existingBalance?.enteredUnitCode ?? baseUnitCode;
        const unitRow = itmUnits.find(u => u.unitCode === enteredUnitCode) || baseRow;
        const conversion = Number(unitRow?.conversion) || 1;
        const quantity = enteredQuantity * conversion;
        const unitCost = conversion > 0 ? enteredUnitCost / conversion : enteredUnitCost;
        const totalCost = quantity * unitCost;

        // Skip if entered quantity is zero
        if (enteredQuantity === 0) {
          // Delete existing entries if any
          if (existingBalance?.stockFlowId) {
            const sfDocRef = doc(db, `companies/${activeCompany.id}/stockFlow`, existingBalance.stockFlowId);
            batch.delete(sfDocRef);
          }
          continue;
        }

        totalInventoryValue += totalCost;

        // Get tracking data from modified values
        const batchNumber = values.batchNumber ?? existingBalance?.batchNumber ?? '';
        const serialNumbers = values.serialNumbers ?? existingBalance?.serialNumbers ?? [];
        const expiryDate = values.expiryDate ?? existingBalance?.expiryDate ?? null;
        const trackingType = item?.trackingType || 'None';

        // Create/Update StockFlow entry
        const stockFlowData = {
          transactionId: obTransaction.id,
          tranTypeCode: 'OB',
          docNumber: obTransaction.docNumber,
          docDate: obTransaction.docDate,
          financialYearId: selectedYearId,
          branchId: activeBranch?.id || null,
          branchName: activeBranch?.branchName || null,
          lineNumber: 0,
          itemId: itemId,
          itemCode: item?.itemCode || '',
          itemName: item?.itemName || '',
          description: `Opening Stock - ${item?.itemName || 'Item'}`,
          locationId: locationId,
          locationName: locationName,
          direction: 'in',
          quantity: quantity,
          unitOfMeasure: baseUnitCode,
          // Multi-unit audit fields (mirror what stockFlowHelper writes for transactional rows)
          enteredQuantity: enteredQuantity,
          enteredUnitCode: enteredUnitCode,
          enteredUnitCost: enteredUnitCost,
          enteredUnitPrice: 0,
          conversion: conversion,
          baseUnitCode: baseUnitCode,
          costingMethod: item?.costingMethod || 'weightedAverage',
          unitCost: unitCost,
          totalCost: totalCost,
          unitPrice: 0,
          grossAmount: 0,
          discountPercent: 0,
          discountAmount: 0,
          netAmount: 0,
          taxable: false,
          taxRate: 0,
          taxAmount: 0,
          lineTotal: 0,
          profitAmount: 0,
          // Batch/Serial tracking
          trackingType: trackingType,
          batchNumber: trackingType === 'Batch' ? batchNumber : '',
          expiryDate: trackingType === 'Batch' ? (expiryDate || null) : null,
          serialNumbers: trackingType === 'Serial' ? serialNumbers : [],
          status: 'Posted', // Opening balances are immediately posted
          updatedAt: timestamp,
          updatedBy: userProfile?.uid
        };

        if (existingBalance?.stockFlowId) {
          const sfDocRef = doc(db, `companies/${activeCompany.id}/stockFlow`, existingBalance.stockFlowId);
          batch.update(sfDocRef, stockFlowData);
        } else {
          stockFlowData.createdAt = timestamp;
          stockFlowData.createdBy = userProfile?.uid;
          const newSfDocRef = doc(stockFlowRef);
          batch.set(newSfDocRef, stockFlowData);
        }
      }

      // Include existing (unmodified) inventory values AT THIS location in total
      // (entryOpeningValue sums batch lines for batch items, qty×cost otherwise).
      for (const [itemId, existing] of Object.entries(inventoryBalances)) {
        if (!modItems[itemId]) {
          totalInventoryValue += entryOpeningValue(existing);
        }
      }

      // CRITICAL (multi-location): add stock value held at OTHER locations, which
      // this save does not touch, so the consolidated Inventory Control opening
      // value always equals total stock value across EVERY location. Without this
      // the books would be overwritten with only the current location's value.
      allOBStock.forEach(sf => {
        const rowLoc = sf.locationId || defaultLocationId || '';
        if (rowLoc !== locationId) {
          totalInventoryValue += Number(sf.totalCost) || ((Number(sf.quantity) || 0) * (Number(sf.unitCost) || 0));
        }
      });

      // Create consolidated SubTransaction for total inventory value
      // This debits Inventory Control account for the total value
      if (totalInventoryValue > 0) {
        const existingInvSubTrans = existingSubTransactions.find(
          st => st.accountId === inventoryControlAccount.id && st.description?.includes('Opening Inventory')
        );

        const subTransData = {
          transactionId: obTransaction.id,
          tranTypeCode: 'OB',
          docNumber: obTransaction.docNumber,
          docDate: obTransaction.docDate,
          financialYearId: selectedYearId,
          branchId: activeBranch?.id || null,
          branchName: activeBranch?.branchName || null,
          lineNumber: 999, // High line number for inventory total
          accountId: inventoryControlAccount.id,
          accountCode: inventoryControlAccount.code,
          accountName: inventoryControlAccount.name,
          description: 'Opening Inventory Value',
          debitAmount: totalInventoryValue,
          creditAmount: 0,
          status: 'Posted', // Opening balances are immediately posted
          updatedAt: timestamp,
          updatedBy: userProfile?.uid
        };

        if (existingInvSubTrans?.id) {
          const stDocRef = doc(db, `companies/${activeCompany.id}/subTransactions`, existingInvSubTrans.id);
          batch.update(stDocRef, subTransData);
        } else {
          subTransData.createdAt = timestamp;
          subTransData.createdBy = userProfile?.uid;
          const newStDocRef = doc(subTransRef);
          batch.set(newStDocRef, subTransData);
        }
      }

      // Update parent OB transaction status to 'Posted' (branchId already set at creation)
      const obDocRef = doc(db, `companies/${activeCompany.id}/transactions`, obTransaction.id);
      batch.update(obDocRef, {
        status: 'Posted',
        postedAt: timestamp,
        postedBy: userProfile?.uid,
        updatedAt: timestamp,
        updatedBy: userProfile?.uid
      });

      await batch.commit();

      // Stamp the averageCost/currentStock snapshot on the item cards — the same
      // recalc purchases run. Covers EVERY item with opening stock (not just the
      // rows edited now) so previously-entered openings self-heal on any save.
      try {
        const allItemIds = new Set(Object.keys(modItems));
        allOBStock.forEach(sf => { if (sf.itemId) allItemIds.add(sf.itemId); });
        await refreshItemCostSnapshots(activeCompany.id, [...allItemIds], selectedYearId);
      } catch (snapErr) {
        // Snapshot stamping is display-only; books are already saved correctly.
        console.error('Item cost snapshot refresh failed:', snapErr);
      }

      setModifiedItems({});
      await fetchOpeningBalances();
      alert('Inventory opening balances saved successfully!');
    } catch (error) {
      console.error('Error saving item balances:', error);
      alert('Failed to save item balances: ' + error.message);
    }
    setSaving(false);
  };

  /**
   * Import opening STOCK from an Excel file (same template used by Chart of
   * Inventory). Reads only Item Code + Opening Stock + Opening Cost, matches by
   * Item Code to existing items, and posts against the branch + location the
   * user is currently standing on. Reuses saveItemBalances so all posting rules
   * (unit conversion, consolidated Inventory Control total, and re-import =
   * UPDATE not stack) stay in one place.
   */
  const handleImportInventoryOB = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ''; // let the same file be re-selected later
    if (!file) return;

    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!selectedYearId || !obTransaction?.id) { alert('Please select a financial year first.'); return; }
    if (activeBranch && activeBranch.id === 'all') { alert('Please select a specific branch (not "All Branches") before importing opening stock.'); return; }
    if (openingNotReady()) return;

    // Resolve the target location exactly the way saveItemBalances does so the
    // confirmation message names the real destination.
    let locationId = selectedLocationId;
    let locationName = '';
    if (!locationId && locations.length > 0) {
      const defaultLoc = locations.find(l => l.isDefault) || locations[0];
      locationId = defaultLoc.id; locationName = defaultLoc.locationName;
    } else {
      const loc = locations.find(l => l.id === locationId);
      locationName = loc?.locationName || 'Default';
    }
    const branchName = activeBranch?.branchName || 'Main (single branch)';

    setImportingOB(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const dataSheetName = workbook.SheetNames.find(n => n !== 'Instructions') || workbook.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[dataSheetName]);
      if (!rows.length) { alert('No data found in the Excel file.'); setImportingOB(false); return; }

      // Index existing items by code (case-insensitive)
      const byCode = {};
      items.forEach(it => { if (it.itemCode) byCode[it.itemCode.toString().toLowerCase().trim()] = it; });

      const matched = {};          // itemId -> { quantity, unitCost, unitCode } | { unitCode, batches:[] }
      const notFound = [];         // codes with no matching item
      const serialSkipped = [];    // serial items (still manual)
      const issues = [];           // batch/plain validation problems
      let matchedCount = 0;

      // Group rows by item first, so a Batch item can span MULTIPLE rows (one per batch).
      const grouped = {}; // itemId -> { item, rows: [{ batchNo, qty, cost, expiry, rowNum }] }
      rows.forEach((row, idx) => {
        const code = (row['Item Code'] ?? '').toString().trim();
        if (!code) return;
        const item = byCode[code.toLowerCase()];
        if (!item) { notFound.push(code); return; }
        const qty = parseFloat(row['Opening Stock']) || 0;
        if (qty <= 0) return; // blank/zero row — skip
        let cost = parseFloat(row['Opening Cost']);
        if (!cost || cost <= 0) cost = Number(item.purchasePrice) || Number(item.averageCost) || 0;
        const batchNo = (row['Batch No'] ?? row['Batch Number'] ?? '').toString().trim();
        const expiry = ((row['Expiry Date'] ?? row['Expiry'] ?? '').toString().trim()) || null;
        if (!grouped[item.id]) grouped[item.id] = { item, rows: [] };
        grouped[item.id].rows.push({ batchNo, qty, cost, expiry, rowNum: idx + 2 });
      });

      Object.values(grouped).forEach(({ item, rows: irows }) => {
        const units = Array.isArray(item.units) ? item.units : [];
        const baseUnit = (units.find(u => u.isBase) || units[0])?.unitCode || item.unitOfMeasure || 'Pcs';

        // Serial items still need per-unit data — not bulk-openable here.
        if (item.trackingType === 'Serial') { serialSkipped.push(item.itemCode); return; }

        if (item.trackingType === 'Batch') {
          const seen = new Set();
          const batches = [];
          irows.forEach(r => {
            if (!r.batchNo) { issues.push(`${item.itemCode} (row ${r.rowNum}): Batch No is required for a batch-tracked item.`); return; }
            const key = r.batchNo.toLowerCase();
            if (seen.has(key)) { issues.push(`${item.itemCode} (row ${r.rowNum}): duplicate Batch No "${r.batchNo}".`); return; }
            seen.add(key);
            batches.push({ batchNumber: r.batchNo, quantity: r.qty, unitCost: r.cost, expiryDate: r.expiry });
          });
          if (batches.length === 0) return; // nothing valid for this item
          matched[item.id] = { unitCode: baseUnit, batches };
          matchedCount++;
          return;
        }

        // Plain (None): one row expected; if several, use the first and flag the rest.
        if (irows.length > 1) issues.push(`${item.itemCode}: ${irows.length} rows for a non-batch item — used the first, ignored the rest.`);
        matched[item.id] = { quantity: irows[0].qty, unitCost: irows[0].cost, unitCode: baseUnit };
        matchedCount++;
      });

      if (matchedCount === 0) {
        alert(
          `No opening stock to import.\n\n` +
          `• Item codes not found: ${notFound.length}\n` +
          `• Serial items skipped (enter manually): ${serialSkipped.length}\n` +
          `• Rows with issues: ${issues.length}\n\n` +
          (issues.length ? issues.slice(0, 12).join('\n') + (issues.length > 12 ? '\n…' : '') + '\n\n' : '') +
          `Tip: import items into Chart of Inventory first; for Batch items add one row per batch with a unique Batch No.`
        );
        setImportingOB(false);
        return;
      }

      const msg =
        `Import opening stock for ${matchedCount} item(s) into:\n\n` +
        `   Branch:   ${branchName}\n` +
        `   Location: ${locationName}\n\n` +
        (notFound.length ? `• ${notFound.length} row(s) skipped — item code not found.\n` : '') +
        (serialSkipped.length ? `• ${serialSkipped.length} serial item(s) skipped — enter these manually.\n` : '') +
        (issues.length ? `• ${issues.length} row issue(s):\n   ${issues.slice(0, 8).join('\n   ')}${issues.length > 8 ? '\n   …' : ''}\n` : '') +
        `\nBatch items: all batches at this location are replaced with the file (no double-up).\n\nContinue?`;

      if (!window.confirm(msg)) { setImportingOB(false); return; }

      await saveItemBalances(matched); // shows its own success alert + refreshes
    } catch (err) {
      console.error('OB inventory import failed:', err);
      alert('Import failed: ' + err.message);
    } finally {
      setImportingOB(false);
    }
  };

  /**
   * Export the opening stock currently SHOWING on the grid (selected location)
   * as an Excel file in the exact format the import reads (Item Code / Opening
   * Stock / Opening Cost), so it can be re-imported on another branch/location.
   * Quantities/costs are exported in BASE units — the import treats them as base,
   * so a round-trip (export → clear → import) reproduces identical stock value.
   */
  const handleExportInventoryOB = () => {
    const rows = [];
    allOBStock.forEach(sf => {
      if (!sf.itemId) return;
      const rowLoc = sf.locationId || defaultLocationId || '';
      if (selectedLocationId && rowLoc !== selectedLocationId) return;
      const qty = Number(sf.quantity) || 0;
      if (qty <= 0) return;
      rows.push({
        'Item Code': sf.itemCode || '',
        'Item Name': sf.itemName || '',
        'Opening Stock': qty,
        'Opening Cost': Number(sf.unitCost) || 0
      });
    });

    if (rows.length === 0) { alert('Nothing to export — no opening stock at this location.'); return; }

    const locName = locations.find(l => l.id === (selectedLocationId || defaultLocationId))?.locationName || 'Default';
    const brName = activeBranch?.branchName || 'Main';
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Items');
    XLSX.writeFile(wb, `Opening Stock - ${brName} - ${locName}.xlsx`);
  };

  /**
   * Download a blank opening-stock template with EVERY inventory item pre-filled
   * (Item Code / Item Name / Unit). The user only fills Opening Stock + Opening
   * Cost, then imports. Import reads Item Code + Opening Stock + Opening Cost.
   */
  const downloadInventoryOBTemplate = () => {
    if (!items.length) { alert('No inventory items found. Add items in Chart of Inventory first.'); return; }
    const rows = items.map(it => ({
      'Item Code': it.itemCode || '',
      'Item Name': it.itemName || '',
      'Unit': it.unitOfMeasure || '',
      'Tracking': it.trackingType || 'None',
      'Batch No': '',
      'Opening Stock': '',
      'Opening Cost': '',
      'Expiry Date': ''
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 16 }, { wch: 34 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Opening Stock');

    const instr = [
      { Instructions: '=== OPENING STOCK IMPORT TEMPLATE ===' }, { Instructions: '' },
      { Instructions: 'All your items are already listed. Fill Opening Stock (quantity) + Opening Cost (per unit).' },
      { Instructions: 'Blank Opening Cost falls back to the item purchase/average price. Leave a row 0/blank to skip.' }, { Instructions: '' },
      { Instructions: '── BATCH items (Tracking = Batch) ──' },
      { Instructions: '   Add ONE ROW PER BATCH: copy the item row, keep the SAME Item Code, and give each row a' },
      { Instructions: '   unique Batch No, its Opening Stock, Opening Cost, and (optional) Expiry Date.' },
      { Instructions: '   Example: two rows with the same Item Code but Batch No B-2401 and B-2402 = two batches.' }, { Instructions: '' },
      { Instructions: 'Serial items (Tracking = Serial) are NOT imported here — enter those manually on the grid.' },
      { Instructions: 'Import reads: Item Code, Batch No, Opening Stock, Opening Cost, Expiry Date. (Item Name / Unit / Tracking are reference only.)' }, { Instructions: '' },
      { Instructions: 'THEN: Opening Balances > Inventory > Import Excel — pick the correct Branch + Location first.' }
    ];
    const wsI = XLSX.utils.json_to_sheet(instr);
    wsI['!cols'] = [{ wch: 105 }];
    XLSX.utils.book_append_sheet(wb, wsI, 'Instructions');
    XLSX.writeFile(wb, 'Opening_Stock_Template.xlsx');
  };

  /**
   * Clear ALL opening stock currently showing on the grid (selected location,
   * current branch) after admin-password approval. Reuses saveItemBalances with
   * every item's qty forced to 0, so row deletion AND the consolidated Inventory
   * Control value correction go through the one tested engine.
   */
  const handleClearInventoryOB = async () => {
    if (!clearPassword) { alert('Enter the admin password.'); return; }
    setClearBusy(true);
    try {
      const ok = await verifyAdminPassword({
        companyId: activeCompany?.id, currentUser, companyUser, activeCompany, password: clearPassword,
      });
      if (!ok) { alert('Incorrect admin password.'); setClearBusy(false); return; }

      const overrides = {};
      Object.entries(inventoryBalances).forEach(([itemId, bal]) => {
        // Batch items: an empty batch list tells the save engine to delete every
        // batch row for that item. Others: force qty 0 (deletes the single row).
        if (Array.isArray(bal?.batches)) overrides[itemId] = { trackingType: 'Batch', batches: [] };
        else if (bal?.stockFlowId) overrides[itemId] = { quantity: 0 };
      });
      if (Object.keys(overrides).length === 0) { alert('Nothing to clear at this location.'); setClearBusy(false); setShowClearModal(false); return; }

      await saveItemBalances(overrides); // deletes rows + fixes accounting + refreshes snapshots
      setShowClearModal(false);
      setClearPassword('');
    } catch (err) {
      console.error('Clear opening inventory failed:', err);
      alert('Failed to clear: ' + err.message);
    } finally {
      setClearBusy(false);
    }
  };

  // Import closing balances from previous year as opening balances
  const importFromPreviousYear = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (activeBranch && activeBranch.id === 'all') { alert('Please select a specific branch (not "All Branches") before importing.'); return; }
    if (openingNotReady()) return;
    // Auto-detect previous year if not linked
    let previousYearId = selectedYear?.previousYearId;
    if (!previousYearId) {
      // Find the most recent closed year that ended before this year started
      const thisStart = new Date(selectedYear?.startDate);
      const candidates = financialYears
        .filter(y => y.id !== selectedYearId && new Date(y.endDate) < thisStart)
        .sort((a, b) => new Date(b.endDate) - new Date(a.endDate));
      previousYearId = candidates[0]?.id || null;

      if (!previousYearId) {
        alert('No previous financial year found to import from');
        return;
      }

      // Update the year record with the detected previousYearId for future use
      try {
        const yearDocRef = doc(db, `companies/${activeCompany.id}/financialYears`, selectedYearId);
        await updateDoc(yearDocRef, { previousYearId });
        setSelectedYear(prev => ({ ...prev, previousYearId }));
      } catch (e) {
        console.warn('Could not update previousYearId on year record:', e);
      }
    }

    const prevYearName = financialYears.find(y => y.id === previousYearId)?.yearName || 'previous year';
    const scopeBranch = activeBranch?.branchName || 'Main (single branch)';
    if (!window.confirm(
      `Import closing balances of "${prevYearName}" as opening balances of "${selectedYear?.yearName || 'this year'}"\n\n` +
      `>>> FOR BRANCH: ${scopeBranch} <<<\n` +
      `(Only this branch's opening is written. Repeat per branch if you have more.)\n\n` +
      `Permanent accounts (Asset, Liability, Equity) carry forward their net balances.\n` +
      `Revenue & Expense accounts close to zero, with net profit/loss transferred to Retained Earnings.\n\nContinue?`
    )) {
      return;
    }

    setSaving(true);
    try {
      const currentBranchId = activeBranch?.id || null;

      // Classification below needs the FULL chart of accounts (including Revenue &
      // Expense). The on-screen `accounts` state is deliberately filtered to
      // Asset/Liability/Equity for the manual OB grid — using it here misclassified
      // every P&L account as "missing", so net profit was never computed and never
      // reached Retained Earnings (the opening then came out unbalanced by exactly
      // the profit). Fetch all accounts fresh and classify from this map.
      const allAcctSnap = await getDocs(collection(db, `companies/${activeCompany.id}/accounts`));
      const allAccountsById = new Map();
      allAcctSnap.docs.forEach(d => allAccountsById.set(d.id, { id: d.id, ...d.data() }));

      // Step 1: Fetch SubTransactions for the previous financial year (filtered by branch)
      const prevSubTransQuery = query(
        collection(db, `companies/${activeCompany.id}/subTransactions`),
        where('financialYearId', '==', previousYearId)
      );
      const prevSubTransSnap = await getDocs(prevSubTransQuery);

      if (prevSubTransSnap.empty) {
        alert('No transactions found in previous year');
        setSaving(false);
        return;
      }

      // Fetch previous-year stock rows up-front too (used in Step 6) so the
      // untagged-entries decision below can cover BOTH ledger and stock.
      const prevStockQuery = query(
        collection(db, `companies/${activeCompany.id}/stockFlow`),
        where('financialYearId', '==', previousYearId)
      );
      const prevStockSnap = await getDocs(prevStockQuery);

      // Old entries created BEFORE branches existed carry no branch tag. If we
      // silently included them for every branch, running this import on 4
      // branches would count them 4 times. So: ask ONCE, and the user should
      // say YES on exactly ONE branch (their main one).
      let includeUntagged = true;
      if (currentBranchId) {
        const untaggedCount =
          prevSubTransSnap.docs.filter(d => !d.data().branchId && d.data().status !== 'Cancelled').length +
          prevStockSnap.docs.filter(d => !d.data().branchId && d.data().status !== 'Cancelled').length;
        if (untaggedCount > 0) {
          includeUntagged = window.confirm(
            `${untaggedCount} old entr(ies) from the previous year have NO branch tag (created before branches were set up).\n\n` +
            `Include them in THIS branch's opening (${activeBranch?.branchName})?\n\n` +
            `IMPORTANT: answer OK on ONE branch only — including them in multiple branches would count them more than once.\n\n` +
            `OK = include here   |   Cancel = leave them out of this branch`
          );
        } else {
          includeUntagged = false; // nothing untagged; keep the strict per-branch rule
        }
      }

      // Step 2: Aggregate debits and credits per account (filtered by branch)
      const accountTotals = {};
      prevSubTransSnap.docs.forEach(d => {
        const data = d.data();
        if (data.status === 'Cancelled') return;
        // Branch filter: this branch's entries always; untagged only if approved above
        if (currentBranchId && data.branchId && data.branchId !== currentBranchId) return;
        if (currentBranchId && !data.branchId && !includeUntagged) return;
        const accId = data.accountId;
        if (!accId) return;

        if (!accountTotals[accId]) {
          accountTotals[accId] = {
            accountId: accId,
            accountCode: data.accountCode || '',
            accountName: data.accountName || '',
            partyType: data.partyType || null,
            partyId: data.partyId || null,
            partyName: data.partyName || null,
            totalDebit: 0,
            totalCredit: 0
          };
        }
        accountTotals[accId].totalDebit += Number(data.debitAmount) || 0;
        accountTotals[accId].totalCredit += Number(data.creditAmount) || 0;
      });

      // Step 3: Separate permanent vs temporary accounts, calculate net profit/loss
      let netProfitLoss = 0; // positive = profit (credit), negative = loss (debit)
      const permanentBalances = [];
      // Accounts that existed last year but are missing from the current COA —
      // their balances CANNOT carry forward; report them instead of dropping silently.
      const missingAccounts = [];

      Object.values(accountTotals).forEach(accTotal => {
        const account = allAccountsById.get(accTotal.accountId);
        const netAmount = accTotal.totalDebit - accTotal.totalCredit;
        if (!account) {
          if (Math.abs(netAmount) >= 0.01) {
            missingAccounts.push({ name: accTotal.accountName || accTotal.accountCode || accTotal.accountId, amount: netAmount });
          }
          return;
        }
        const accountType = (account?.accountType || '').toLowerCase();

        if (['revenue', 'expense'].includes(accountType)) {
          // Temporary accounts: accumulate net into profit/loss
          // Revenue net is typically credit (negative netAmount = profit)
          // Expense net is typically debit (positive netAmount = cost)
          netProfitLoss -= netAmount; // Revenue credits increase profit, Expense debits decrease it
        } else if (['asset', 'liability', 'equity'].includes(accountType)) {
          // Permanent accounts: carry forward net balance
          if (Math.abs(netAmount) < 0.01) return; // Skip zero balances

          // Natural debit accounts (asset): positive net = debit balance
          // Natural credit accounts (liability, equity): negative net = credit balance
          let debitAmount = 0;
          let creditAmount = 0;
          if (netAmount > 0) {
            debitAmount = Math.round(netAmount * 100) / 100;
          } else {
            creditAmount = Math.round(Math.abs(netAmount) * 100) / 100;
          }

          permanentBalances.push({
            ...accTotal,
            debitAmount,
            creditAmount
          });
        }
      });

      // Sanity check BEFORE writing: the opening we're about to create should
      // balance. It can be off when inter-branch entries have one leg in another
      // branch, or when deleted accounts dropped a balance. Warn, let user decide.
      const chkDr = permanentBalances.reduce((s, b) => s + (b.debitAmount || 0), 0) + (netProfitLoss < 0 ? Math.abs(netProfitLoss) : 0);
      const chkCr = permanentBalances.reduce((s, b) => s + (b.creditAmount || 0), 0) + (netProfitLoss > 0 ? netProfitLoss : 0);
      const chkDiff = Math.round((chkDr - chkCr) * 100) / 100;
      if (Math.abs(chkDiff) >= 0.01) {
        const missingMsg = missingAccounts.length
          ? `\nDeleted/missing accounts that could not carry forward:\n${missingAccounts.slice(0, 8).map(m => `• ${m.name} (${Math.abs(m.amount).toFixed(2)})`).join('\n')}${missingAccounts.length > 8 ? `\n…and ${missingAccounts.length - 8} more` : ''}\n`
          : '';
        if (!window.confirm(
          `WARNING: the imported opening will be UNBALANCED.\n\nDebits ${chkDr.toFixed(2)} vs Credits ${chkCr.toFixed(2)} (difference ${Math.abs(chkDiff).toFixed(2)}).\n${missingMsg}\nLikely causes: inter-branch entries (one leg sits in another branch) or deleted accounts.\nYou can continue and correct with a manual opening entry, or cancel to investigate first.\n\nContinue anyway?`
        )) {
          setSaving(false);
          return;
        }
      }

      // Step 4: Build batch for new year's OB entries
      // Firestore caps a WriteBatch at 500 ops — big COAs/inventories exceed it,
      // so queue ops across auto-rotated batches (same pattern as saveItemBalances).
      const fullBatches = [];
      let currentBatch = writeBatch(db);
      let opCount = 0;
      const batch = {
        set: (ref, data) => { if (opCount >= 450) { fullBatches.push(currentBatch); currentBatch = writeBatch(db); opCount = 0; } opCount++; currentBatch.set(ref, data); },
        update: (ref, data) => { if (opCount >= 450) { fullBatches.push(currentBatch); currentBatch = writeBatch(db); opCount = 0; } opCount++; currentBatch.update(ref, data); },
        delete: (ref) => { if (opCount >= 450) { fullBatches.push(currentBatch); currentBatch = writeBatch(db); opCount = 0; } opCount++; currentBatch.delete(ref); },
        commit: async () => { fullBatches.push(currentBatch); for (const b of fullBatches) await b.commit(); }
      };
      const subTransRef = collection(db, `companies/${activeCompany.id}/subTransactions`);
      const timestamp = new Date().toISOString();
      let lineNumber = 1;

      // Delete existing OB SubTransactions for THIS branch only (don't touch other branches)
      const existingObSubTrans = query(
        collection(db, `companies/${activeCompany.id}/subTransactions`),
        where('transactionId', '==', obTransaction.id)
      );
      const existingSnap = await getDocs(existingObSubTrans);
      existingSnap.docs.forEach(d => {
        const data = d.data();
        // Only delete entries for this branch (or untagged ones for this OB)
        if (!currentBranchId || !data.branchId || data.branchId === currentBranchId) {
          batch.delete(d.ref);
        }
      });
      // This branch's lines that were saved inside ANOTHER branch's opening by
      // mistake are part of its old opening too — clear them, or the re-import
      // leaves this branch's opening counted twice in reports.
      const misplacedOld = currentBranchId
        ? await listMisplacedOpening(activeCompany.id, selectedYearId, currentBranchId)
        : { sub: [], stock: [] };
      misplacedOld.sub.forEach(r => batch.delete(doc(db, `companies/${activeCompany.id}/subTransactions`, r.id)));

      // Write permanent account balances
      permanentBalances.forEach(bal => {
        // Inventory Control must carry the label the manual inventory save looks
        // for ('Opening Inventory Value'); otherwise a later stock edit would not
        // find this entry and would ADD a second one → inventory counted twice.
        const isInvCtrl = bal.accountCode === SYSTEM_ACCOUNTS.INVENTORY_CONTROL;
        const newSubTrans = {
          transactionId: obTransaction.id,
          tranTypeCode: 'OB',
          docNumber: obTransaction.docNumber,
          docDate: obTransaction.docDate,
          financialYearId: selectedYearId,
          branchId: currentBranchId,
          branchName: activeBranch?.branchName || null,
          lineNumber: lineNumber++,
          accountId: bal.accountId,
          accountCode: bal.accountCode,
          accountName: bal.accountName,
          partyType: bal.partyType || null,
          partyId: bal.partyId || null,
          partyName: bal.partyName || null,
          description: isInvCtrl ? 'Opening Inventory Value' : `Opening Balance - ${bal.accountName}`,
          debitAmount: bal.debitAmount,
          creditAmount: bal.creditAmount,
          status: 'Posted',
          importedFrom: previousYearId,
          createdAt: timestamp,
          createdBy: userProfile?.uid,
          updatedAt: timestamp,
          updatedBy: userProfile?.uid
        };
        const newDocRef = doc(subTransRef);
        batch.set(newDocRef, newSubTrans);
      });

      // Step 5: Transfer net profit/loss to Retained Earnings
      if (Math.abs(netProfitLoss) >= 0.01) {
        const retainedEarningsAccount = [...allAccountsById.values()].find(a => a.code === SYSTEM_ACCOUNTS.RETAINED_EARNINGS);
        if (retainedEarningsAccount) {
          const plEntry = {
            transactionId: obTransaction.id,
            tranTypeCode: 'OB',
            docNumber: obTransaction.docNumber,
            docDate: obTransaction.docDate,
            financialYearId: selectedYearId,
            branchId: currentBranchId,
            branchName: activeBranch?.branchName || null,
            lineNumber: lineNumber++,
            accountId: retainedEarningsAccount.id,
            accountCode: retainedEarningsAccount.code,
            accountName: retainedEarningsAccount.name,
            partyType: null,
            partyId: null,
            partyName: null,
            description: 'Net Profit/Loss transferred from previous year',
            debitAmount: netProfitLoss < 0 ? Math.round(Math.abs(netProfitLoss) * 100) / 100 : 0,
            creditAmount: netProfitLoss > 0 ? Math.round(netProfitLoss * 100) / 100 : 0,
            status: 'Posted',
            importedFrom: previousYearId,
            createdAt: timestamp,
            createdBy: userProfile?.uid,
            updatedAt: timestamp,
            updatedBy: userProfile?.uid
          };
          const plDocRef = doc(subTransRef);
          batch.set(plDocRef, plEntry);
        } else {
          // Don't silently swallow the profit — that would leave the opening
          // unbalanced. Abort before anything commits and tell the user how to fix.
          alert(`Net profit/loss of ${Math.abs(netProfitLoss).toFixed(2)} could not be transferred: the Retained Earnings account (${SYSTEM_ACCOUNTS.RETAINED_EARNINGS}) is missing.\n\nRun Master Data → Chart of Accounts → Repair COA, then import again.`);
          setSaving(false);
          return;
        }
      }

      // Step 6: Import StockFlow - calculate net stock per item (filtered by branch)
      // (prevStockSnap already fetched up-front, next to the ledger fetch)

      // Delete existing OB StockFlow for THIS branch only
      const existingObStock = query(
        collection(db, `companies/${activeCompany.id}/stockFlow`),
        where('transactionId', '==', obTransaction.id)
      );
      const existingStockSnap = await getDocs(existingObStock);
      const removedObItemIds = new Set(); // INV-04: their averages are rebuilt too
      existingStockSnap.docs.forEach(d => {
        const data = d.data();
        if (!currentBranchId || !data.branchId || data.branchId === currentBranchId) {
          batch.delete(d.ref);
          if (data.itemId) removedObItemIds.add(data.itemId);
        }
      });
      misplacedOld.stock.forEach(r => {
        batch.delete(doc(db, `companies/${activeCompany.id}/stockFlow`, r.id));
        if (r.itemId) removedObItemIds.add(r.itemId);
      });

      // Aggregate stock per item+location (filtered by branch)
      const stockTotals = {};
      prevStockSnap.docs.forEach(d => {
        const data = d.data();
        if (data.status === 'Cancelled') return;
        if (currentBranchId && data.branchId && data.branchId !== currentBranchId) return;
        if (currentBranchId && !data.branchId && !includeUntagged) return;
        const key = `${data.itemId}_${data.locationId || 'default'}`;
        if (!stockTotals[key]) {
          stockTotals[key] = {
            itemId: data.itemId,
            itemCode: data.itemCode || '',
            itemName: data.itemName || '',
            locationId: data.locationId || '',
            locationName: data.locationName || '',
            unitOfMeasure: data.unitOfMeasure || 'Pcs',
            costingMethod: data.costingMethod || 'weightedAverage',
            totalQtyIn: 0,
            totalQtyOut: 0,
            totalCostIn: 0,
            totalCostOut: 0
          };
        }
        const qty = Number(data.quantity) || 0;
        const cost = Number(data.totalCost) || 0;
        if (data.direction === 'in') {
          stockTotals[key].totalQtyIn += qty;
          stockTotals[key].totalCostIn += cost;
        } else {
          stockTotals[key].totalQtyOut += qty;
          stockTotals[key].totalCostOut += cost;
        }
      });

      const stockFlowRef = collection(db, `companies/${activeCompany.id}/stockFlow`);
      let stockLine = 1;

      Object.values(stockTotals).forEach(st => {
        const netQty = st.totalQtyIn - st.totalQtyOut;
        if (netQty <= 0) return; // Skip items with zero or negative stock

        const netCost = st.totalCostIn - st.totalCostOut;
        const unitCost = netQty > 0 ? Math.round((netCost / netQty) * 100) / 100 : 0;

        const newStockFlow = {
          transactionId: obTransaction.id,
          tranTypeCode: 'OB',
          docNumber: obTransaction.docNumber,
          docDate: obTransaction.docDate,
          financialYearId: selectedYearId,
          branchId: currentBranchId,
          branchName: activeBranch?.branchName || null,
          lineNumber: stockLine++,
          itemId: st.itemId,
          itemCode: st.itemCode,
          itemName: st.itemName,
          description: `Opening Stock - ${st.itemName}`,
          locationId: st.locationId,
          locationName: st.locationName,
          direction: 'in',
          quantity: Math.round(netQty * 100) / 100,
          unitOfMeasure: st.unitOfMeasure,
          costingMethod: st.costingMethod,
          unitCost: unitCost,
          totalCost: Math.round(netCost * 100) / 100,
          unitPrice: 0,
          grossAmount: 0,
          discountPercent: 0,
          discountAmount: 0,
          netAmount: 0,
          taxable: false,
          taxRate: 0,
          taxAmount: 0,
          lineTotal: 0,
          profitAmount: 0,
          status: 'Posted',
          importedFrom: previousYearId,
          createdAt: timestamp,
          createdBy: userProfile?.uid,
          updatedAt: timestamp,
          updatedBy: userProfile?.uid
        };
        const newDocRef = doc(stockFlowRef);
        batch.set(newDocRef, newStockFlow);
      });

      // Step 7: Update OB transaction status to Posted
      const obDocRef = doc(db, `companies/${activeCompany.id}/transactions`, obTransaction.id);
      batch.update(obDocRef, {
        status: 'Posted',
        postedAt: timestamp,
        postedBy: userProfile?.uid,
        updatedAt: timestamp,
        updatedBy: userProfile?.uid
      });

      await batch.commit();

      // Stamp avg cost / current stock snapshots on the imported items (same
      // recalc purchases run) so Item Master reflects the new opening at once.
      try {
        const importedItemIds = Object.values(stockTotals)
          .filter(st => (st.totalQtyIn - st.totalQtyOut) > 0)
          .map(st => st.itemId);
        await refreshItemCostSnapshots(activeCompany.id, [...new Set([...importedItemIds, ...removedObItemIds])], selectedYearId);
      } catch (snapErr) {
        console.error('Item cost snapshot refresh failed:', snapErr);
      }

      await fetchOpeningBalances();

      const profitLossMsg = Math.abs(netProfitLoss) >= 0.01
        ? `\nNet ${netProfitLoss > 0 ? 'Profit' : 'Loss'}: ${Math.abs(netProfitLoss).toLocaleString('en-PK', { minimumFractionDigits: 2 })} transferred to Retained Earnings.`
        : '';
      const missingNote = missingAccounts.length
        ? `\n\nNOTE: ${missingAccounts.length} account(s) from last year no longer exist in the COA and could NOT carry forward — review the warning shown earlier.`
        : '';
      alert(`Opening balances imported from previous year successfully!\n\nBranch: ${scopeBranch}\n${permanentBalances.length} account balance(s) carried forward.${profitLossMsg}${missingNote}`);
    } catch (error) {
      console.error('Error importing from previous year:', error);
      alert('Failed to import from previous year: ' + error.message);
    }
    setSaving(false);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Calculate totals for accounts
  const accountTotals = accounts.reduce((acc, account) => {
    const modified = modifiedAccounts[account.id] || {};
    const stored = accountBalances[account.id] || {};

    const debit = modified.debitAmount ?? stored.debitAmount ?? 0;
    const credit = modified.creditAmount ?? stored.creditAmount ?? 0;

    acc.debit += debit;
    acc.credit += credit;
    return acc;
  }, { debit: 0, credit: 0 });

  const isBalanced = Math.abs(accountTotals.debit - accountTotals.credit) < 0.01;

  // Filter data based on search
  const filteredAccounts = accounts.filter(a =>
    a.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.linkedEntityName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredItems = items.filter(i =>
    i.itemCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.itemName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get modified count for each tab
  const getModifiedCount = (tab) => {
    switch (tab) {
      case 'accounts': return Object.keys(modifiedAccounts).length;
      case 'inventory': return Object.keys(modifiedItems).length;
      default: return 0;
    }
  };

  // Calculate inventory total (batch items sum their batch lines).
  const inventoryTotal = items.reduce((sum, item) => {
    const modified = modifiedItems[item.id];
    const stored = inventoryBalances[item.id] || {};
    if (Array.isArray(modified?.batches)) return sum + entryOpeningValue(modified);
    if (!modified && Array.isArray(stored.batches)) return sum + entryOpeningValue(stored);
    const qty = modified?.quantity ?? stored.quantity ?? 0;
    const cost = modified?.unitCost ?? stored.unitCost ?? 0;
    return sum + (qty * cost);
  }, 0);

  // Get account type label with linked info
  const getAccountLabel = (account) => {
    if (account.linkedEntityType === 'customer') {
      return 'Customer';
    } else if (account.linkedEntityType === 'vendor') {
      return 'Vendor';
    } else if (account.linkedEntityType === 'bank') {
      return 'Bank';
    }
    return account.accountType;
  };

  return (
    <PageLayout pageTitle="Opening Balances" breadcrumb="Master Data / Opening Balances">
      <Container>
        {/* Financial Year Selector */}
        <YearSelector>
          <YearLabel>
            <FiCalendar /> Financial Year:
          </YearLabel>
          <YearSelect
            value={selectedYearId}
            onChange={(e) => handleYearChange(e.target.value)}
          >
            {financialYears.length === 0 ? (
              <option value="">No financial years found</option>
            ) : (
              financialYears.map(year => (
                <option key={year.id} value={year.id}>
                  {year.yearName || year.yearCode} {year.isActive ? '(Active)' : ''} {year.isClosed ? '(Closed)' : ''}
                </option>
              ))
            )}
          </YearSelect>
          {activeBranch && activeBranch.id !== 'all' && (
            <TransactionInfo>
              Branch: {activeBranch.branchName}
            </TransactionInfo>
          )}
          {obTransaction && (
            <TransactionInfo>
              OB Transaction: {obTransaction.docNumber}
            </TransactionInfo>
          )}
          {activeBranch && activeBranch.id === 'all' && (
            <BranchWarning>
              <FiAlertCircle /> Please select a specific branch to manage opening balances
            </BranchWarning>
          )}
          {financialYears.length > 1 && !selectedYear?.isClosed && canEdit && (
            <ImportButton onClick={importFromPreviousYear} disabled={saving || repairing || !obReady}>
              <FiDownload /> Import from Previous Year
            </ImportButton>
          )}
        </YearSelector>

        {!selectedYearId ? (
          <WarningBox>
            <FiAlertCircle />
            <span>Please create a financial year in Settings before entering opening balances.</span>
          </WarningBox>
        ) : selectedYear?.isClosed ? (
          <WarningBox>
            <FiAlertCircle />
            <span>This financial year is closed. Opening balances cannot be modified.</span>
          </WarningBox>
        ) : (
          <>
            {orphanOpening && canEdit && (
              <WarningBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#FFF7E6', borderColor: '#F0B400' }}>
                <span><FiAlertCircle /> Opening balances exist that aren't linked to any branch (entered before this branch was created).</span>
                <ImportButton onClick={handleRelinkOrphan} disabled={relinking}>
                  <FiDownload /> {relinking ? 'Moving…' : `Move into ${activeBranch?.branchName || 'this branch'}`}
                </ImportButton>
              </WarningBox>
            )}
            {misplaced && canEdit && (
              <WarningBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#FFF1F0', borderColor: '#E5484D' }}>
                <span><FiAlertCircle /> {misplaced.accounts + misplaced.stock} opening entr(ies) of this branch were saved inside another branch's opening by mistake — reports count this branch's opening twice.</span>
                <ImportButton onClick={handleRepairMisplaced} disabled={repairing || saving || !obReady}>
                  <FiCheckCircle /> {repairing ? 'Fixing…' : 'Fix now'}
                </ImportButton>
              </WarningBox>
            )}
            <HeaderSection>
              <TabList>
                <Tab
                  $active={activeTab === 'accounts'}
                  onClick={() => { setActiveTab('accounts'); setSearchTerm(''); }}
                >
                  <FiDollarSign /> Accounts
                  {getModifiedCount('accounts') > 0 && (
                    <ModifiedBadge>{getModifiedCount('accounts')}</ModifiedBadge>
                  )}
                </Tab>
                <Tab
                  $active={activeTab === 'inventory'}
                  onClick={() => { setActiveTab('inventory'); setSearchTerm(''); }}
                >
                  <FiPackage /> Inventory
                  {getModifiedCount('inventory') > 0 && (
                    <ModifiedBadge>{getModifiedCount('inventory')}</ModifiedBadge>
                  )}
                </Tab>
              </TabList>

              <SearchBox>
                <FiSearch />
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <ClearButton onClick={() => setSearchTerm('')}>
                    <FiX />
                  </ClearButton>
                )}
              </SearchBox>
            </HeaderSection>

            {/* Accounts Tab */}
            {activeTab === 'accounts' && (
              <>
                <BalanceSummary $balanced={isBalanced}>
                  <SummaryItem>
                    <SummaryLabel>Total Accounts</SummaryLabel>
                    <SummaryValue>{accounts.length}</SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Total Debits</SummaryLabel>
                    <SummaryValue>{activeCompany?.currencyCode || 'PKR'} {formatCurrency(accountTotals.debit)}</SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Total Credits</SummaryLabel>
                    <SummaryValue>{activeCompany?.currencyCode || 'PKR'} {formatCurrency(accountTotals.credit)}</SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Difference</SummaryLabel>
                    <DifferenceValue $balanced={isBalanced}>
                      {isBalanced ? (
                        <><FiCheckCircle /> Balanced</>
                      ) : (
                        <><FiAlertCircle /> {activeCompany?.currencyCode || 'PKR'} {formatCurrency(Math.abs(accountTotals.debit - accountTotals.credit))}</>
                      )}
                    </DifferenceValue>
                  </SummaryItem>
                  {canEdit && (
                    <SaveButton onClick={saveAccountBalances} disabled={saving || repairing || !obReady || Object.keys(modifiedAccounts).length === 0}>
                      <FiSave /> {saving ? 'Saving...' : 'Save Accounts'}
                    </SaveButton>
                  )}
                </BalanceSummary>

                <InfoText>
                  All Chart of Accounts including Customers, Vendors, and Banks are shown below.
                  Enter opening balances as Debit or Credit based on the account's normal balance.
                </InfoText>

                <TableContainer>
                  {loading ? (
                    <LoadingMessage>Loading accounts...</LoadingMessage>
                  ) : filteredAccounts.length === 0 ? (
                    <EmptyMessage>
                      {searchTerm ? 'No accounts found matching your search.' : 'No postable accounts found. Please create accounts in Chart of Accounts first.'}
                    </EmptyMessage>
                  ) : (
                    <Table>
                      <thead>
                        <tr>
                          <Th>Code</Th>
                          <Th>Account Name</Th>
                          <Th>Type</Th>
                          <Th style={{ textAlign: 'right' }}>Debit (Dr)</Th>
                          <Th style={{ textAlign: 'right' }}>Credit (Cr)</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAccounts.map(account => {
                          const modified = modifiedAccounts[account.id] || {};
                          const stored = accountBalances[account.id] || {};

                          const debitValue = modified.debitAmount ?? stored.debitAmount ?? 0;
                          const creditValue = modified.creditAmount ?? stored.creditAmount ?? 0;
                          const isModified = modifiedAccounts[account.id] !== undefined;

                          return (
                            <Tr key={account.id} $modified={isModified}>
                              <Td>
                                <AccountCode>{account.code}</AccountCode>
                              </Td>
                              <Td>
                                <AccountName>
                                  {account.name}
                                  {account.linkedEntityName && (
                                    <LinkedEntity>({account.linkedEntityName})</LinkedEntity>
                                  )}
                                </AccountName>
                              </Td>
                              <Td>
                                <TypeBadge $type={account.linkedEntityType || account.accountType}>
                                  {getAccountLabel(account)}
                                </TypeBadge>
                              </Td>
                              <Td>
                                <BalanceInput
                                  type="number"
                                  value={debitValue || ''}
                                  onChange={(e) => handleAccountBalanceChange(account.id, 'debitAmount', e.target.value)}
                                  placeholder="0.00"
                                  $modified={isModified}
                                  disabled={selectedYear?.isClosed || !canEdit}
                                />
                              </Td>
                              <Td>
                                <BalanceInput
                                  type="number"
                                  value={creditValue || ''}
                                  onChange={(e) => handleAccountBalanceChange(account.id, 'creditAmount', e.target.value)}
                                  placeholder="0.00"
                                  $modified={isModified}
                                  disabled={selectedYear?.isClosed || !canEdit}
                                />
                              </Td>
                            </Tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </TableContainer>
              </>
            )}

            {/* Inventory Tab */}
            {activeTab === 'inventory' && (
              <>
                <BalanceSummary>
                  <SummaryItem>
                    <SummaryLabel>Total Items</SummaryLabel>
                    <SummaryValue>{items.length}</SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Total Inventory Value</SummaryLabel>
                    <SummaryValue>{activeCompany?.currencyCode || 'PKR'} {formatCurrency(inventoryTotal)}</SummaryValue>
                  </SummaryItem>
                  {locations.length > 1 && (
                    <LocationSelect>
                      <label>Location:</label>
                      <select
                        value={selectedLocationId}
                        onChange={(e) => setSelectedLocationId(e.target.value)}
                      >
                        {locations.map(loc => (
                          <option key={loc.id} value={loc.id}>
                            {loc.locationName} {loc.isDefault ? '(Default)' : ''}
                          </option>
                        ))}
                      </select>
                    </LocationSelect>
                  )}
                  {canEdit && (
                    <>
                      <input
                        ref={obFileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={handleImportInventoryOB}
                      />
                      <ImportButton onClick={downloadInventoryOBTemplate} disabled={saving || importingOB} style={{ marginLeft: 'auto' }} title="Excel with all items pre-filled — just enter opening qty & cost">
                        <FiDownload /> Download Template
                      </ImportButton>
                      <ImportButton onClick={() => obFileInputRef.current?.click()} disabled={saving || importingOB || repairing || !obReady} style={{ marginLeft: 0 }} title="Upload the filled Excel to set opening stock">
                        <FiUpload /> {importingOB ? 'Importing…' : 'Import Excel'}
                      </ImportButton>
                      <ImportButton onClick={handleExportInventoryOB} disabled={saving || importingOB} style={{ marginLeft: 0 }} title="Download the opening stock already entered at this location">
                        <FiDownload /> Export Current
                      </ImportButton>
                      <ClearAllButton onClick={() => { setClearPassword(''); setShowClearModal(true); }} disabled={saving || importingOB || repairing || !obReady || Object.keys(inventoryBalances).length === 0}>
                        <FiTrash2 /> Clear
                      </ClearAllButton>
                      <SaveButton onClick={() => saveItemBalances()} disabled={saving || repairing || !obReady || Object.keys(modifiedItems).length === 0} style={{ marginLeft: 0 }}>
                        <FiSave /> {saving ? 'Saving...' : 'Save Inventory'}
                      </SaveButton>
                    </>
                  )}
                </BalanceSummary>

                {showClearModal && (
                  <ClearOverlay onClick={() => !clearBusy && setShowClearModal(false)}>
                    <ClearBox onClick={e => e.stopPropagation()}>
                      <h3><FiAlertCircle /> Clear Opening Inventory</h3>
                      <p>
                        This will remove <b>ALL {Object.keys(inventoryBalances).length} opening stock item(s)</b> shown for:
                      </p>
                      <p style={{ margin: '4px 0' }}>
                        <b>Branch:</b> {activeBranch?.branchName || 'Main (single branch)'}<br />
                        <b>Location:</b> {locations.find(l => l.id === (selectedLocationId || defaultLocationId))?.locationName || 'Default'}
                      </p>
                      <p>
                        The opening accounting value will be corrected automatically. Other locations are not touched.
                        Export first if you need a backup of these figures.
                      </p>
                      <input
                        type="password"
                        placeholder="Admin password"
                        value={clearPassword}
                        onChange={e => setClearPassword(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleClearInventoryOB(); }}
                        autoFocus
                      />
                      <ClearActions>
                        <button className="cancel" onClick={() => setShowClearModal(false)} disabled={clearBusy}>Cancel</button>
                        <button className="danger" onClick={handleClearInventoryOB} disabled={clearBusy || !clearPassword}>
                          {clearBusy ? 'Clearing…' : 'Clear Opening Stock'}
                        </button>
                      </ClearActions>
                    </ClearBox>
                  </ClearOverlay>
                )}

                <InfoText>
                  All inventory items from Chart of Inventory are shown below.
                  Enter opening quantity and unit cost to set the starting inventory value.
                </InfoText>

                <TableContainer>
                  {loading ? (
                    <LoadingMessage>Loading items...</LoadingMessage>
                  ) : filteredItems.length === 0 ? (
                    <EmptyMessage>
                      {searchTerm ? 'No items found matching your search.' : 'No items found. Please create items in Chart of Inventory first.'}
                    </EmptyMessage>
                  ) : (
                    <Table>
                      <thead>
                        <tr>
                          <Th>Item Code</Th>
                          <Th>Item Name</Th>
                          <Th>Tracking</Th>
                          <Th>Unit</Th>
                          <Th style={{ textAlign: 'right' }}>Opening Qty</Th>
                          <Th style={{ textAlign: 'right' }}>Unit Cost</Th>
                          <Th style={{ textAlign: 'right' }}>Total Value</Th>
                          <Th>Batch / Serial</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.map(item => {
                          const modified = modifiedItems[item.id] || {};
                          const stored = inventoryBalances[item.id] || {};

                          const quantity = modified.quantity ?? stored.quantity ?? 0;
                          const unitCost = modified.unitCost ?? stored.unitCost ?? 0;
                          const totalValue = quantity * unitCost;
                          const isModified = modifiedItems[item.id] !== undefined;
                          const trackingType = item.trackingType || 'None';
                          const batchNumber = modified.batchNumber ?? stored.batchNumber ?? '';
                          const expiryDate = modified.expiryDate ?? stored.expiryDate ?? '';
                          const serialNumbers = modified.serialNumbers ?? stored.serialNumbers ?? [];
                          const serialText = modified.serialText !== undefined ? modified.serialText : serialNumbers.join(', ');

                          // Batch-tracked item → one editable row per batch line, plus a
                          // "+ Add batch" footer row showing the item's combined total.
                          if (trackingType === 'Batch') {
                            const itmUnits = Array.isArray(item.units) ? item.units : [];
                            const baseRow = itmUnits.find(u => u.isBase) || itmUnits[0];
                            const baseUnit = baseRow?.unitCode || item.unitOfMeasure || 'Pcs';
                            const currentUnit = modified.unitCode || stored.unitCode || baseUnit;
                            const rows = getEditableBatches(item.id);
                            const itemTotalValue = rows.reduce((s, b) => s + (Number(b.quantity) || 0) * (Number(b.unitCost) || 0), 0);
                            const itemTotalQty = rows.reduce((s, b) => s + (Number(b.quantity) || 0), 0);
                            const rowDisabled = selectedYear?.isClosed || !canEdit;
                            return (
                              <React.Fragment key={item.id}>
                                {rows.map((b, bi) => (
                                  <Tr key={b.key ?? bi} $modified={isModified}>
                                    <Td>{bi === 0 ? <AccountCode>{item.itemCode}</AccountCode> : ''}</Td>
                                    <Td>{bi === 0 ? item.itemName : ''}</Td>
                                    <Td>{bi === 0 ? <TrackingBadge $type="Batch">Batch</TrackingBadge> : ''}</Td>
                                    <Td>{bi === 0 ? (itmUnits.length > 1 ? (
                                      <select value={currentUnit} onChange={(e) => handleItemBalanceChange(item.id, 'unitCode', e.target.value)}
                                        style={{ padding: '2px 4px', fontSize: '11px', border: `1px solid ${colors.border.main}`, borderRadius: '4px' }}>
                                        {itmUnits.map(u => (<option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>))}
                                      </select>
                                    ) : baseUnit) : ''}</Td>
                                    <Td>
                                      <BalanceInput type="number" step="any" value={b.quantity || ''}
                                        onChange={(e) => handleBatchFieldChange(item.id, bi, 'quantity', e.target.value)}
                                        placeholder="0" $modified={isModified} style={{ width: '100px' }} disabled={rowDisabled} />
                                    </Td>
                                    <Td>
                                      <BalanceInput type="number" value={b.unitCost || ''}
                                        onChange={(e) => handleBatchFieldChange(item.id, bi, 'unitCost', e.target.value)}
                                        placeholder="0.00" $modified={isModified} style={{ width: '120px' }} disabled={rowDisabled} />
                                    </Td>
                                    <Td style={{ textAlign: 'right' }}>
                                      <TotalValue>{activeCompany?.currencyCode || 'PKR'} {formatCurrency((Number(b.quantity) || 0) * (Number(b.unitCost) || 0))}</TotalValue>
                                    </Td>
                                    <Td>
                                      <TrackingInputGroup>
                                        <BalanceInput type="text" value={b.batchNumber || ''}
                                          onChange={(e) => handleBatchFieldChange(item.id, bi, 'batchNumber', e.target.value)}
                                          placeholder="Batch #" $modified={isModified} style={{ width: '100px' }} disabled={rowDisabled} />
                                        <BalanceInput type="date" value={b.expiryDate || ''}
                                          onChange={(e) => handleBatchFieldChange(item.id, bi, 'expiryDate', e.target.value)}
                                          $modified={isModified} style={{ width: '130px' }} disabled={rowDisabled} title="Expiry Date" />
                                        <button type="button" title="Remove batch" disabled={rowDisabled}
                                          onClick={() => removeBatchRow(item.id, bi)}
                                          style={{ border: 'none', background: 'transparent', color: colors.error.main, cursor: rowDisabled ? 'default' : 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
                                      </TrackingInputGroup>
                                    </Td>
                                  </Tr>
                                ))}
                                <Tr $modified={isModified}>
                                  <Td /><Td /><Td />
                                  <Td style={{ textAlign: 'right', fontSize: '11px', color: colors.text.tertiary }}>Total</Td>
                                  <Td style={{ textAlign: 'right', fontSize: '11px', color: colors.text.secondary }}>{itemTotalQty}</Td>
                                  <Td />
                                  <Td style={{ textAlign: 'right' }}>
                                    <TotalValue>{activeCompany?.currencyCode || 'PKR'} {formatCurrency(itemTotalValue)}</TotalValue>
                                  </Td>
                                  <Td>
                                    <button type="button" disabled={rowDisabled} onClick={() => addBatchRow(item.id)}
                                      style={{ border: `1px dashed ${colors.border.main}`, background: 'transparent', color: colors.primary?.main || '#800020', cursor: rowDisabled ? 'default' : 'pointer', fontSize: 12, borderRadius: 4, padding: '3px 8px' }}>
                                      + Add batch
                                    </button>
                                  </Td>
                                </Tr>
                              </React.Fragment>
                            );
                          }

                          return (
                            <Tr key={item.id} $modified={isModified}>
                              <Td>
                                <AccountCode>{item.itemCode}</AccountCode>
                              </Td>
                              <Td>{item.itemName}</Td>
                              <Td>
                                {trackingType !== 'None' ? (
                                  <TrackingBadge $type={trackingType}>{trackingType}</TrackingBadge>
                                ) : (
                                  <span style={{ color: colors.text.tertiary, fontSize: '11px' }}>None</span>
                                )}
                              </Td>
                              <Td>{(() => {
                                const itmUnits = Array.isArray(item.units) ? item.units : [];
                                const baseRow = itmUnits.find(u => u.isBase) || itmUnits[0];
                                const baseUnit = baseRow?.unitCode || item.unitOfMeasure || 'Pcs';
                                const currentUnit = modified.unitCode || stored.unitCode || baseUnit;
                                if (itmUnits.length > 1) {
                                  return (
                                    <select
                                      value={currentUnit}
                                      onChange={(e) => handleItemBalanceChange(item.id, 'unitCode', e.target.value)}
                                      style={{ padding: '2px 4px', fontSize: '11px', border: `1px solid ${colors.border.main}`, borderRadius: '4px' }}
                                    >
                                      {itmUnits.map(u => (
                                        <option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>
                                      ))}
                                    </select>
                                  );
                                }
                                return baseUnit;
                              })()}</Td>
                              <Td>
                                <BalanceInput
                                  type="number"
                                  step="any"
                                  value={quantity || ''}
                                  onChange={(e) => handleItemBalanceChange(item.id, 'quantity', e.target.value)}
                                  placeholder="0"
                                  $modified={isModified}
                                  style={{ width: '100px' }}
                                  disabled={selectedYear?.isClosed || !canEdit}
                                />
                              </Td>
                              <Td>
                                <BalanceInput
                                  type="number"
                                  value={unitCost || ''}
                                  onChange={(e) => handleItemBalanceChange(item.id, 'unitCost', e.target.value)}
                                  placeholder="0.00"
                                  $modified={isModified}
                                  style={{ width: '120px' }}
                                  disabled={selectedYear?.isClosed || !canEdit}
                                />
                              </Td>
                              <Td style={{ textAlign: 'right' }}>
                                <TotalValue>{activeCompany?.currencyCode || 'PKR'} {formatCurrency(totalValue)}</TotalValue>
                              </Td>
                              <Td>
                                {trackingType === 'Batch' ? (
                                  <TrackingInputGroup>
                                    <BalanceInput
                                      type="text"
                                      value={batchNumber}
                                      onChange={(e) => handleItemBalanceChange(item.id, 'batchNumber', e.target.value)}
                                      placeholder="Batch #"
                                      $modified={isModified}
                                      style={{ width: '100px' }}
                                      disabled={selectedYear?.isClosed || !canEdit}
                                    />
                                    <BalanceInput
                                      type="date"
                                      value={expiryDate}
                                      onChange={(e) => handleItemBalanceChange(item.id, 'expiryDate', e.target.value)}
                                      $modified={isModified}
                                      style={{ width: '130px' }}
                                      disabled={selectedYear?.isClosed || !canEdit}
                                      title="Expiry Date"
                                    />
                                  </TrackingInputGroup>
                                ) : trackingType === 'Serial' ? (
                                  <BalanceInput
                                    type="text"
                                    value={serialText}
                                    onChange={(e) => handleItemBalanceChange(item.id, 'serialText', e.target.value)}
                                    onBlur={(e) => {
                                      const parsed = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                                      handleItemBalanceChange(item.id, 'serialNumbers', parsed);
                                    }}
                                    placeholder={quantity > 0 ? `Enter ${quantity} serial(s), comma-separated` : 'Enter serials'}
                                    $modified={isModified}
                                    style={{ width: '220px' }}
                                    disabled={selectedYear?.isClosed || !canEdit}
                                  />
                                ) : (
                                  <span style={{ color: colors.text.tertiary, fontSize: '11px' }}>-</span>
                                )}
                              </Td>
                            </Tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </TableContainer>
              </>
            )}
          </>
        )}
      </Container>
    </PageLayout>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const YearSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: ${colors.background.default};
  padding: ${spacing.md} ${spacing.lg};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  flex-wrap: wrap;
`;

const YearLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};

  svg {
    color: ${colors.primary.main};
  }
`;

const YearSelect = styled.select`
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  background: ${colors.background.default};
  color: ${colors.text.primary};
  min-width: 250px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const TransactionInfo = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  background: ${colors.background.paper};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borderRadius.sm};
`;

const ClearAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.error.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover:not(:disabled) { background: ${colors.error.dark}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ClearOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ClearBox = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
  width: min(440px, 92vw);
  box-shadow: 0 10px 40px rgba(0,0,0,0.25);

  h3 {
    display: flex;
    align-items: center;
    gap: ${spacing.xs};
    color: ${colors.error.main};
    margin: 0 0 ${spacing.md} 0;
    font-size: ${typography.fontSize.lg};
  }
  p {
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.secondary};
    margin: 0 0 ${spacing.sm} 0;
  }
  input {
    width: 100%;
    padding: ${spacing.sm} ${spacing.md};
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSize.md};
    margin: ${spacing.sm} 0;
  }
`;

const ClearActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  margin-top: ${spacing.sm};

  button {
    padding: ${spacing.sm} ${spacing.lg};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.medium};
  }
  .cancel {
    background: ${colors.background.paper};
    color: ${colors.text.primary};
  }
  .danger {
    background: ${colors.error.main};
    color: ${colors.text.white};
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
`;

const ImportButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.info.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  margin-left: auto;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${colors.info.dark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WarningBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: ${colors.warning.main}15;
  border: 1px solid ${colors.warning.main}30;
  border-radius: ${borderRadius.md};
  padding: ${spacing.lg};
  color: ${colors.warning.dark};

  svg {
    font-size: 24px;
    flex-shrink: 0;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;
`;

const TabList = styled.div`
  display: flex;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.lg};
  background: ${props => props.$active ? colors.primary.main : 'transparent'};
  color: ${props => props.$active ? colors.text.white : colors.text.secondary};
  border: 1px solid ${props => props.$active ? colors.primary.main : colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? colors.primary.dark : colors.background.hover};
  }

  svg {
    font-size: 18px;
  }
`;

const ModifiedBadge = styled.span`
  background: ${colors.warning.main};
  color: ${colors.text.white};
  padding: 2px 6px;
  border-radius: ${borderRadius.full};
  font-size: 10px;
  font-weight: ${typography.fontWeight.bold};
  margin-left: 4px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  min-width: 250px;

  svg {
    color: ${colors.text.secondary};
  }

  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: ${typography.fontSize.sm};
    background: transparent;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${colors.background.hover};
  }
`;

const BalanceSummary = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};
  background: ${props => props.$balanced === false ? colors.error.main + '10' : colors.background.default};
  padding: ${spacing.md} ${spacing.lg};
  border-radius: ${borderRadius.md};
  border: 1px solid ${props => props.$balanced === false ? colors.error.main + '30' : colors.border.light};
  flex-wrap: wrap;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const SummaryValue = styled.span`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
`;

const DifferenceValue = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.bold};
  color: ${props => props.$balanced ? colors.success.main : colors.error.main};

  svg {
    font-size: 18px;
  }
`;

const LocationSelect = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};

  label {
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.secondary};
  }

  select {
    padding: ${spacing.xs} ${spacing.sm};
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.sm};
    font-size: ${typography.fontSize.sm};
    background: ${colors.background.default};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  margin-left: auto;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoText = styled.p`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  background: ${colors.info.main}10;
  border: 1px solid ${colors.info.main}20;
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  margin: 0;
`;

const TableContainer = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  overflow: hidden;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: ${spacing.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  background: ${colors.background.paper};
  border-bottom: 1px solid ${colors.border.light};
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.border.light};
  background: ${props => props.$modified ? colors.warning.main + '08' : 'transparent'};
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.$modified ? colors.warning.main + '12' : colors.background.hover};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: ${spacing.sm} ${spacing.md};
  vertical-align: middle;
`;

const AccountCode = styled.span`
  font-family: monospace;
  font-size: ${typography.fontSize.xs};
  color: ${colors.primary.main};
  background: ${colors.primary.main}10;
  padding: 2px 6px;
  border-radius: ${borderRadius.sm};
`;

const AccountName = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

const LinkedEntity = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  font-style: italic;
`;

const TypeBadge = styled.span`
  font-size: ${typography.fontSize.xs};
  padding: 2px 8px;
  border-radius: ${borderRadius.sm};
  text-transform: capitalize;
  background: ${props => {
    const typeColors = {
      asset: colors.success.main + '15',
      liability: colors.warning.main + '15',
      equity: colors.info.main + '15',
      customer: colors.primary.main + '15',
      vendor: colors.secondary?.main ? colors.secondary.main + '15' : colors.warning.main + '15',
      bank: colors.info.main + '15',
    };
    return typeColors[props.$type] || colors.background.paper;
  }};
  color: ${props => {
    const typeColors = {
      asset: colors.success.main,
      liability: colors.warning.main,
      equity: colors.info.main,
      customer: colors.primary.main,
      vendor: colors.secondary?.main || colors.warning.main,
      bank: colors.info.main,
    };
    return typeColors[props.$type] || colors.text.secondary;
  }};
`;

const BalanceInput = styled.input`
  width: 140px;
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${props => props.$modified ? colors.warning.main : colors.border.main};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  text-align: right;
  background: ${props => props.$modified ? colors.warning.main + '10' : colors.background.default};
  color: ${colors.text.primary};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 2px ${colors.primary.main}20;
  }

  &::placeholder {
    color: ${colors.text.disabled};
  }

  &:disabled {
    background: ${colors.background.paper};
    cursor: not-allowed;
  }
`;

const TotalValue = styled.span`
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};
`;

const BranchWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${colors.warning.main}15;
  color: ${colors.warning.main};
  border: 1px solid ${colors.warning.main}30;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
`;

const TrackingBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${borderRadius.sm};
  font-size: 10px;
  font-weight: ${typography.fontWeight.semibold};
  text-transform: uppercase;
  background: ${props => props.$type === 'Batch' ? '#E3F2FD' : props.$type === 'Serial' ? '#F3E5F5' : colors.neutral[100]};
  color: ${props => props.$type === 'Batch' ? '#1565C0' : props.$type === 'Serial' ? '#7B1FA2' : colors.text.secondary};
`;

const TrackingInputGroup = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

export default OpeningBalances;
